import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function BlogSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const blogs = [
    {
      id: 1,
      title: 'Fiber Optik Kablo Tamiri Nasıl Yapılır?',
      excerpt: 'Fiber optik kablo tamiri için gerekli ekipmanlar, fusion splicing teknikleri ve OTDR test yöntemleri hakkında detaylı rehber.',
      category: 'Fiber Optik',
      date: '15 Ocak 2025',
      author: 'Teknik Ekip',
      image: 'https://public.readdy.ai/ai/img_res/9a0e986aac7f51a22fea8f20524769f9.jpg',
      readTime: '8 dk',
    },
    {
      id: 2,
      title: 'IP Kamera Seçim Rehberi',
      category: 'Güvenlik',
      excerpt: 'İşletmeniz için doğru IP kamera sistemini seçerken dikkat etmeniz gereken teknik özellikler, çözünürlük, gece görüşü ve depolama seçenekleri.',
      date: '12 Ocak 2025',
      author: 'Güvenlik Uzmanı',
      image: 'https://readdy.ai/api/search-image?query=IP%20security%20camera%20installation%20modern%20surveillance%20system%20professional%20setup%20high%20resolution%20camera%20mounted%20on%20wall&width=800&height=450&seq=blog-002&orientation=landscape',
      readTime: '6 dk',
    },
    {
      id: 3,
      title: 'OTDR Testi Nedir ve Neden Önemlidir?',
      category: 'Fiber Optik',
      excerpt: 'OTDR (Optical Time Domain Reflectometer) test cihazı ile fiber optik kablo hatalarının tespiti, kayıp ölçümü ve arıza lokasyonu belirleme.',
      date: '10 Ocak 2025',
      author: 'Teknik Ekip',
      image: 'https://readdy.ai/api/search-image?query=OTDR%20test%20equipment%20fiber%20optic%20testing%20device%20technician%20analyzing%20results%20professional%20measurement%20tools&width=800&height=450&seq=blog-003&orientation=landscape',
      readTime: '7 dk',
    },
  ];

  return (
    <section id="blog" className="relative py-24 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between mb-12"
        >
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Bilgi Merkezi
            </h2>
            <p className="text-lg text-gray-400">
              Fiber optik ve güvenlik sistemleri hakkında faydalı içerikler
            </p>
          </div>
          <a
            href="#blog-all"
            className="hidden md:flex items-center gap-2 text-primary-400 hover:text-primary-300 font-semibold transition-colors cursor-pointer"
          >
            Tüm Yazılar
            <i className="ri-arrow-right-line"></i>
          </a>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog, index) => (
            <motion.article
              key={blog.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              {/* Featured Image */}
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Category Badge */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold ${
                  blog.category === 'Fiber Optik' 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-emerald-500 text-white'
                }`}>
                  {blog.category}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-dark-900 group-hover:text-primary-500 transition-colors duration-300 line-clamp-2">
                  {blog.title}
                </h3>
                
                <p className="text-sm text-gray-600 line-clamp-3">
                  {blog.excerpt}
                </p>

                {/* Meta Info */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                      <span>{blog.author}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                      <span>{blog.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{blog.readTime}</span>
                  </div>
                </div>

                {/* Read More */}
                <button className="text-primary-500 font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                  Devamını Oku
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Mobile View All Button */}
        <div className="md:hidden mt-8 text-center">
          <a
            href="#blog-all"
            className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-semibold transition-colors cursor-pointer"
          >
            Tüm Yazılar
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}